import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { OrderRound } from '@restaurant/api-client';
import { formatOrderLineChoices } from '@restaurant/types';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  Preloader,
  f7,
} from 'framework7-react';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';
import {
  useAcceptRound,
  useCancelRound,
  useFireRound,
  useMarkRoundReady,
  useReopenRound,
  useResolveServiceRequest,
  useRestoreItem,
  useServeRound,
  useTableSession,
  useVoidItem,
} from '../hooks/useTableOrdering';
import AssignCustomerSheet from './AssignCustomerSheet';
import AttachReservationSheet from './AttachReservationSheet';
import RoundActions from './RoundActions';

type F7Color = 'orange' | 'blue' | 'green' | 'gray' | 'red' | 'primary';

function itemConfigLine(item: OrderRound['items'][number]): string | undefined {
  const parts = item.categoryNameSnapshot ? [item.categoryNameSnapshot] : [];
  parts.push(...formatOrderLineChoices(item.choices));
  return parts.length > 0 ? parts.join(', ') : undefined;
}

export default function TableSessionInspector({ sessionId }: { sessionId: string | null }) {
  const tAll = useT();
  const t = tAll.tableOrdering.inspector;
  const { data: session, isLoading } = useTableSession(sessionId);
  const fireRound = useFireRound();
  const acceptRound = useAcceptRound();
  const markRoundReady = useMarkRoundReady();
  const serveRound = useServeRound();
  const reopenRound = useReopenRound();
  const cancelRound = useCancelRound();
  const voidItem = useVoidItem();
  const restoreItem = useRestoreItem();
  const resolveRequest = useResolveServiceRequest();

  const ROUND_META: Record<OrderRound['status'], { label: string; color: F7Color }> = {
    PLACED: { label: t.roundStatus.placed, color: 'orange' },
    FIRED: { label: t.roundStatus.fired, color: 'blue' },
    READY: { label: t.roundStatus.ready, color: 'green' },
    SERVED: { label: t.roundStatus.served, color: 'gray' },
    CANCELLED: { label: t.roundStatus.cancelled, color: 'red' },
  };

  const [attachOpen, setAttachOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [actionRound, setActionRound] = useState<OrderRound | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const roundPopoverRef = useRef<any>(null);

  if (!sessionId) {
    return (
      <Block style={{ paddingTop: 32 }}>
        <EmptyState icon="hand_point_left" title={t.selectTable} description={t.selectTableDesc} />
      </Block>
    );
  }
  if (isLoading || !session) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }

  const currency = session.currency;
  const closed = session.status === 'CLOSED';
  const cust = session.customer;
  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const fire = (r: OrderRound) =>
    fireRound.mutate(
      { sessionId: session.id, roundId: r.id },
      { onSuccess: () => toast(t.roundFired(r.sequence)) },
    );
  const accept = (r: OrderRound) =>
    acceptRound.mutate(
      { sessionId: session.id, roundId: r.id },
      { onSuccess: () => toast(t.roundAccepted(r.sequence)) },
    );
  const markReady = (r: OrderRound) =>
    markRoundReady.mutate(
      { sessionId: session.id, roundId: r.id },
      { onSuccess: () => toast(t.roundReadyToast(r.sequence)) },
    );
  const serve = (r: OrderRound) =>
    serveRound.mutate(
      { sessionId: session.id, roundId: r.id },
      { onSuccess: () => toast(t.roundServedToast(r.sequence)) },
    );
  const reopen = (r: OrderRound) =>
    reopenRound.mutate(
      { sessionId: session.id, roundId: r.id },
      { onSuccess: () => toast(t.roundReopened(r.sequence)) },
    );
  const openRoundActions = (r: OrderRound, target: HTMLElement) => {
    setActionRound(r);
    requestAnimationFrame(() => {
      if (roundPopoverRef.current?.el) f7.popover.open(roundPopoverRef.current.el, target);
    });
  };
  const cancel = (r: OrderRound) =>
    f7.dialog.prompt(t.cancelRoundPrompt, tAll.tableOrdering.roundActions.cancelRound, (reason) => {
      if (!reason.trim()) return;
      cancelRound.mutate(
        { sessionId: session.id, roundId: r.id, body: { reason: reason.trim() } },
        { onSuccess: () => toast(t.roundCancelledToast(r.sequence)) },
      );
    });
  const voidLine = (itemId: string, name: string) =>
    f7.dialog.prompt(t.voidItemPrompt(name), t.voidItemTitle, (reason) => {
      const trimmed = reason.trim();
      voidItem.mutate(
        { sessionId: session.id, itemId, body: trimmed ? { reason: trimmed } : {} },
        { onSuccess: () => toast(tAll.tableOrdering.timeline.itemVoided) },
      );
    });
  const restoreLine = (itemId: string) =>
    restoreItem.mutate(
      { sessionId: session.id, itemId },
      { onSuccess: () => toast(tAll.tableOrdering.timeline.itemRestored) },
    );
  const resolve = (requestId: string) =>
    resolveRequest.mutate({ sessionId: session.id, requestId });

  const pendingRequests = session.serviceRequests.filter((r) => r.status === 'PENDING');
  // Newest round on top - the operator's worklist (what was just fired/added is
  // first, no scrolling on a busy tab). Backend stays sequence-asc; we reverse a
  // copy here. The "Round N" labels keep the true order legible either way.
  const visibleRounds = session.rounds
    .filter((r) => r.items.length > 0)
    .slice()
    .reverse();

  const statusChip: { text: string; color: F7Color } =
    session.status === 'BILL_REQUESTED'
      ? { text: t.billRequestedStatus, color: 'orange' }
      : session.status === 'OPEN'
        ? { text: t.openStatus, color: 'green' }
        : { text: t.closedStatus, color: 'gray' };

  return (
    <div className="to-insp">
      {/* Header */}
      <Card outline className="to-insp-hd">
        <CardContent>
          <div className="to-insp-hd__row">
            <h2 className="to-insp-hd__title">{session.tableLabel}</h2>
            <Chip text={statusChip.text} color={statusChip.color} />
          </div>
          <div className="to-insp-hd__meta">
            {cust ? (
              <span
                role="link"
                style={{ color: 'var(--f7-theme-color)', cursor: 'pointer' }}
                onClick={() => f7.views.current.router.navigate(`/customers/${cust.id}`)}
              >
                {cust.name}
                {cust.totalVisits > 0 ? ` · ${t.visitsCount(cust.totalVisits)}` : ''}
              </span>
            ) : (
              <>
                {session.guestName ?? t.walkIn}
                {session.guestPhone ? ` · ${session.guestPhone}` : ''}
              </>
            )}{' '}
            · {t.openedTime} {DateTime.fromISO(session.createdAt).toFormat('HH:mm')}
          </div>
          <div className="to-insp-stats">
            <div className="to-insp-stat">
              <b>{formatMoney(session.totalCents, currency)}</b>
              <span>{t.total}</span>
            </div>
            {/* Paid only appears once a payment diverges it from Total - until
                then Total IS the balance, so a second identical figure is just
                noise. The full balance/paid breakdown lives on the bill page. */}
            {session.paidCents > 0 && (
              <div className="to-insp-stat">
                <b>{formatMoney(session.paidCents, currency)}</b>
                <span>{t.paid}</span>
              </div>
            )}
            <div className="to-insp-stat">
              <b>{visibleRounds.length}</b>
              <span>{t.rounds}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary actions - one dominant CTA over a tonal button grid. The grid
          flexes to its contents: open tabs get Bill / Attach / Activity; a
          closed tab keeps only Activity (the audit timeline is its sole, but
          always-available, action). */}
      <div className="to-insp-actions">
        {!closed && (
          <Button
            fill
            round
            large
            iconF7="plus"
            className="to-insp-actions__cta"
            onClick={() =>
              f7.views.current.router.navigate(`/tables/session/${session.id}/add-round`)
            }
          >
            {t.addRound}
          </Button>
        )}
        <div className="to-insp-actions__grid">
          {!closed && (
            <Button
              round
              iconF7="creditcard"
              className="to-insp-act"
              onClick={() => f7.views.current.router.navigate(`/tables/session/${session.id}/bill`)}
            >
              {t.bill}
            </Button>
          )}
          {!closed && !session.reservationId && (
            <Button round iconF7="link" className="to-insp-act" onClick={() => setAttachOpen(true)}>
              {t.attach}
            </Button>
          )}
          {!closed && (
            <Button
              round
              iconF7="person_crop_circle"
              className="to-insp-act"
              onClick={() => setAssignOpen(true)}
            >
              {cust ? t.guest : t.addGuest}
            </Button>
          )}
          <Button
            round
            iconF7="clock"
            className="to-insp-act"
            onClick={() =>
              f7.views.current.router.navigate(`/tables/session/${session.id}/timeline`)
            }
          >
            {t.activity}
          </Button>
        </div>
      </div>

      {/* Service requests */}
      {pendingRequests.length > 0 && (
        <>
          <BlockTitle>{t.needsAttention}</BlockTitle>
          <List dividers strongIos insetIos>
            {pendingRequests.map((req) => (
              <ListItem
                key={req.id}
                title={req.kind === 'CALL_WAITER' ? t.guestCalledServer : t.guestRequestedBill}
                footer={req.note ?? undefined}
              >
                <i slot="media" className="f7-icons text-color-red" style={{ fontSize: 22 }}>
                  bell_fill
                </i>
                <Button slot="after" small fill round onClick={() => resolve(req.id)}>
                  {t.resolve}
                </Button>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Rounds */}
      <BlockTitle>{t.rounds}</BlockTitle>
      {visibleRounds.length === 0 ? (
        <Block className="text-align-center text-color-gray" style={{ padding: '24px 0' }}>
          {t.noRounds}
        </Block>
      ) : (
        visibleRounds.map((round) => {
          const meta = ROUND_META[round.status];
          // Items can be voided once the round is with the kitchen and beyond -
          // staff retain full control even after it's served (matches the
          // void-fired-item use-case, which allows FIRED/READY/SERVED).
          const itemsEditable =
            !closed &&
            (round.status === 'FIRED' || round.status === 'READY' || round.status === 'SERVED');
          return (
            <Card key={round.id} className="to-round" outline>
              <List dividers className="to-round__list">
                {/* Header row: round number (title) + status (after slot) -
                    laid out by the same ListItem mechanism as the item prices
                    below, which already right-aligns reliably. */}
                <ListItem
                  className="to-round__head"
                  title={t.roundNum(round.sequence)}
                  footer={DateTime.fromISO(round.createdAt).toFormat('HH:mm')}
                >
                  {closed || round.status === 'CANCELLED' ? (
                    <Chip slot="after" text={meta.label} color={meta.color} />
                  ) : (
                    <button
                      slot="after"
                      type="button"
                      className="to-round__status"
                      onClick={(e) => openRoundActions(round, e.currentTarget)}
                    >
                      <Chip text={meta.label} color={meta.color}>
                        <i className="f7-icons to-round__status-caret">chevron_down</i>
                      </Chip>
                    </button>
                  )}
                </ListItem>
                {round.items.map((it) => (
                  <ListItem
                    key={it.id}
                    className={it.voidedAt ? 'to-round__item--void' : undefined}
                    title={`${it.quantity}× ${it.nameSnapshot}`}
                    footer={itemConfigLine(it)}
                  >
                    <div slot="after" className="to-round__price">
                      <span>{formatMoney(it.lineTotalCents, currency)}</span>
                      {itemsEditable &&
                        (it.voidedAt ? (
                          <button
                            type="button"
                            className="to-round__restore"
                            aria-label={t.restoreAria(it.nameSnapshot)}
                            onClick={() => restoreLine(it.id)}
                          >
                            <i className="f7-icons">arrow_counterclockwise</i>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="to-round__void"
                            aria-label={t.voidAria(it.nameSnapshot)}
                            onClick={() => voidLine(it.id, it.nameSnapshot)}
                          >
                            <i className="f7-icons">trash</i>
                          </button>
                        ))}
                    </div>
                  </ListItem>
                ))}
              </List>
              {round.guestNote && <div className="to-round__note">"{round.guestNote}"</div>}
            </Card>
          );
        })
      )}
      <div style={{ height: 24 }} />

      {/* Add-a-round and Bill are pushed routes (not sheets) - each is a
          destination of its own with in-flow navigation (see AddRoundPage /
          BillPage). Attach stays a sheet: a single-screen action, always
          mounted, toggled via `open` (Rule 10). */}
      <AttachReservationSheet
        session={session}
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
      />

      {/* Assign / change / unlink the linked CRM customer - single-screen
          action, always mounted, toggled via `open` (Rule 10). */}
      <AssignCustomerSheet
        session={session}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
      />

      {/* Contextual round-status menu, opened from a round's status chip -
          the same quick-action popover the reservation list uses. */}
      <RoundActions
        ref={roundPopoverRef}
        round={actionRound}
        onClose={() => f7.popover.close()}
        onFire={(r) => (session.status === 'OPEN' ? accept(r) : fire(r))}
        onReady={markReady}
        onServe={serve}
        onReopen={reopen}
        onCancel={cancel}
      />
    </div>
  );
}
